import { Button, Modal, Space, message } from 'antd';
import { errorMessage } from '../api/client';

interface Options {
  code: string;
  fullName: string;
  onDeactivate: () => Promise<unknown>;
  onForceDelete: () => Promise<unknown>;
  deactivatedMessage: string;
  deletedMessage: string;
}

/**
 * Shown when a delete attempt fails because the account has transaction history. A plain
 * Modal.confirm can't hold three distinct choices safely — its X button fires the same handler as
 * Cancel — so this uses an explicit three-button footer instead, where dismissing the modal does
 * nothing. Shared between investors and dealers since the choice is identical either side of the
 * book: deactivate, force-delete the history along with the account, or back out.
 */
export function offerDeactivateOrForceDelete({
  code,
  fullName,
  onDeactivate,
  onForceDelete,
  deactivatedMessage,
  deletedMessage,
}: Options) {
  const modal = Modal.confirm({
    title: `${code} has transaction history`,
    content: `It cannot be safely deleted. Deactivate ${fullName}'s account instead, or permanently delete it along with every transaction on record — this cannot be undone.`,
    icon: null,
    footer: (
      <Space style={{ marginTop: 16, justifyContent: 'flex-end', width: '100%' }}>
        <Button onClick={() => modal.destroy()}>Cancel</Button>
        <Button
          onClick={async () => {
            modal.destroy();
            try {
              await onDeactivate();
              message.success(deactivatedMessage);
            } catch (err) {
              message.error(errorMessage(err));
            }
          }}
        >
          Deactivate
        </Button>
        <Button
          danger
          type="primary"
          onClick={async () => {
            modal.destroy();
            try {
              await onForceDelete();
              message.success(deletedMessage);
            } catch (err) {
              Modal.error({ title: 'Could not delete this account', content: errorMessage(err) });
            }
          }}
        >
          Delete permanently
        </Button>
      </Space>
    ),
  });
}
